import React, { useState, useEffect, useRef } from 'react';
import "../styles/CollapsibleNavbar.css";
import clinExpertLogo from "../ClinExpert_logo.png";
import NotificationCenter from './NotificationCenter';
import { restoreAdminSession } from '../utils/auth';

const CollapsibleNavbar = ({ setCurrentView, userType, isStandaloneAdmin, isAdmin, onFormBuilderClick }) => { // Add onFormBuilderClick prop
    const [preceptorMenuOpen, setPreceptorMenuOpen] = useState(false);
    const [schedulingMenuOpen, setSchedulingMenuOpen] = useState(false);
    const [administrationMenuOpen, setAdministrationMenuOpen] = useState(false);
    const [importMenuOpen, setImportMenuOpen] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false); // Add new state for export menu
    const [exportNestedOpen, setExportNestedOpen] = useState(false);
    const [importNestedOpen, setImportNestedOpen] = useState(false);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const navRef = useRef(null);

    // Get user ID for notifications
    const userId = localStorage.getItem(`${userType}_id`) || 
                   localStorage.getItem('admin_id') ||
                   localStorage.getItem('user_id');

    const broadcastNavbarInteraction = (event) => {
        if (event.target instanceof Element && event.target.closest('.notification-center-wrapper')) {
            return;
        }

        window.dispatchEvent(new CustomEvent('clinexpert-navbar-interaction'));
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (navRef.current && !navRef.current.contains(event.target)) {
                setPreceptorMenuOpen(false);
                setSchedulingMenuOpen(false);
                setAdministrationMenuOpen(false);
                setImportMenuOpen(false);
                setExportMenuOpen(false);
                setExportNestedOpen(false);
                setImportNestedOpen(false);
                setAccountMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleNavbarInteraction = () => {
            setPreceptorMenuOpen(false);
            setSchedulingMenuOpen(false);
            setAdministrationMenuOpen(false);
            setImportMenuOpen(false);
            setExportMenuOpen(false);
            setExportNestedOpen(false);
            setImportNestedOpen(false);
            setAccountMenuOpen(false);
        };

        window.addEventListener('clinexpert-navbar-interaction', handleNavbarInteraction);
        return () => window.removeEventListener('clinexpert-navbar-interaction', handleNavbarInteraction);
    }, []);

    const handleViewChange = (view) => {
        setCurrentView(view);
        setPreceptorMenuOpen(false);
        setSchedulingMenuOpen(false);
        setAdministrationMenuOpen(false);
        setImportMenuOpen(false);
        setExportMenuOpen(false);
        setExportNestedOpen(false);
        setImportNestedOpen(false);
        setAccountMenuOpen(false);
    };

    const handlePreceptorMenuClick = () => {
        setPreceptorMenuOpen(!preceptorMenuOpen);
        setSchedulingMenuOpen(false);
        setAdministrationMenuOpen(false);
        setImportMenuOpen(false);
        setExportMenuOpen(false);
        setExportNestedOpen(false);
        setImportNestedOpen(false);
        setAccountMenuOpen(false);
    };

    const handleSchedulingMenuClick = () => {
        setSchedulingMenuOpen(!schedulingMenuOpen);
        setPreceptorMenuOpen(false);
        setAdministrationMenuOpen(false);
        setImportMenuOpen(false);
        setExportMenuOpen(false);
        setExportNestedOpen(false);
        setImportNestedOpen(false);
        setAccountMenuOpen(false);
    };

    const handleAdministrationMenuClick = () => {
        setAdministrationMenuOpen(!administrationMenuOpen);
        setPreceptorMenuOpen(false);
        setSchedulingMenuOpen(false);
        setImportMenuOpen(false);
        setExportMenuOpen(false);
        setExportNestedOpen(false);
        setImportNestedOpen(false);
        setAccountMenuOpen(false);
    };

    const handleExportMenuClick = () => {
        setExportMenuOpen(!exportMenuOpen);
        setPreceptorMenuOpen(false);
        setAdministrationMenuOpen(false);
        setImportMenuOpen(false);
        setExportNestedOpen(false);
        setImportNestedOpen(false);
        setAccountMenuOpen(false);
    };

    const handleExportNestedClick = (e) => {
        e.stopPropagation();
        setExportNestedOpen(!exportNestedOpen);
        setImportNestedOpen(false);
        setAccountMenuOpen(false);
    };

    const handleImportNestedClick = (e) => {
        e.stopPropagation();
        setImportNestedOpen(!importNestedOpen);
        setExportNestedOpen(false);
        setAccountMenuOpen(false);
    };

    const handleAccountMenuClick = () => {
        setAccountMenuOpen(!accountMenuOpen);
        setPreceptorMenuOpen(false);
        setAdministrationMenuOpen(false);
        setImportMenuOpen(false);
        setExportMenuOpen(false);
        setExportNestedOpen(false);
        setImportNestedOpen(false);
    };

    const handleLogout = () => {

        const restored = restoreAdminSession();
        if (restored.success) {
            window.location.href = restored.returnPath || '/AdminDashboard/manage-users';
            return;
        }

        localStorage.clear();
        window.location.href = '/';
    };

    const ExportUsersDropdown = () => (
        <li className="dropdown">
            <button 
                className={`dropdown-btn ${exportMenuOpen ? 'active' : ''}`}
                onClick={handleExportMenuClick}
            >
                Export Users <span className="arrow">▼</span>
            </button>
            <ul className={`dropdown-content ${exportMenuOpen ? 'show' : ''}`}>
                <li>
                    <button onClick={() => handleViewChange("export-students")}>
                        Export Students
                    </button>
                </li>
                <li>
                    <button onClick={() => handleViewChange("export-preceptors")}>
                        Export Preceptors
                    </button>
                </li>
                <li>
                    <button onClick={() => handleViewChange("export-admins")}>
                        Export Administrators
                    </button>
                </li>
            </ul>
        </li>
    );

    const schedulingMenuItems = [
        { view: "schedule-student", label: "Student Schedules" },
        { view: "schedule-preceptor", label: "Preceptor Schedules" },
        { view: "schedule-speciality", label: "Experience Schedules" },
        { view: "schedule-institution", label: "Institution Schedules" },
        { view: "schedule-rotation", label: "Rotation Schedules" },
        { view: "schedule-reporting", label: "Schedule Reporting" },
        { view: "constraints", label: "Scheduling Constraints" }
    ];

    const renderSchedulingDropdown = () => (
        <li className="dropdown">
            <button 
                className={`dropdown-btn ${schedulingMenuOpen ? 'active' : ''}`}
                onClick={handleSchedulingMenuClick}
            >
                Scheduling <span className="arrow">▼</span>
            </button>
            <ul className={`dropdown-content ${schedulingMenuOpen ? 'show' : ''}`}>
                {schedulingMenuItems.map((item) => (
                    <li key={item.view}>
                        <button onClick={() => handleViewChange(item.view)}>
                            {item.label}
                        </button>
                    </li>
                ))}
            </ul>
        </li>
    );

    return (
        <nav className="navbar" ref={navRef} onClickCapture={broadcastNavbarInteraction}>
            <div className="navbar-left">
                <img src={clinExpertLogo} alt="ClinExpert Logo" className="navbar-logo" style={{height:42}} />
            </div>
            <ul className="nav-list">
                {/* Common options for all */}
                <li>
                    <button onClick={() => handleViewChange("dashboard")}>
                        Dashboard
                    </button>
                </li>
                {/* Replace the My Account button with a dropdown for all users */}
                <li className="dropdown">
                    <button
                        className={`dropdown-btn ${accountMenuOpen ? 'active' : ''}`}
                        onClick={handleAccountMenuClick}
                    >
                        My Account <span className="arrow">▼</span>
                    </button>
                    <ul className={`dropdown-content ${accountMenuOpen ? 'show' : ''}`} style={{ minWidth: 180 }}>
                        <li>
                            <button onClick={() => handleViewChange('my-account')}>Edit Account</button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange('change-password')}>Change Password</button>
                        </li>
                    </ul>
                </li>
                {/* Removed Change Password option */}
                <li>
                    <button onClick={() => handleViewChange('my-organization')}>
                        My Organization
                    </button>
                </li>

                {/* Preceptor-Admin specific options */}
                {!isStandaloneAdmin && userType === 'preceptor' && isAdmin && ( // Add isAdmin check
                    <>
                        {/* My Schedule Section */}
                        <li className="dropdown">
                            <button 
                                className={`dropdown-btn ${preceptorMenuOpen ? 'active' : ''}`}
                                onClick={handlePreceptorMenuClick}
                            >
                                My Schedule <span className="arrow">▼</span>
                            </button>
                            <ul className={`dropdown-content ${preceptorMenuOpen ? 'show' : ''}`}>
                                <li>
                                    <button onClick={() => handleViewChange("preceptor-schedule")}>
                                        My Schedule
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("choices")}>
                                        My Availability
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("my-evaluations")}>
                                        My Evaluations
                                    </button>
                                </li>
                            </ul>
                        </li>

                        {/* Scheduling tab: separate schedule pages + constraints */}
                        {renderSchedulingDropdown()}

                        {/* Administration tab: all other admin pages */}
                        <li className="dropdown">
                            <button 
                                className={`dropdown-btn ${administrationMenuOpen ? 'active' : ''}`}
                                onClick={handleAdministrationMenuClick}
                            >
                                Administration <span className="arrow">▼</span>
                            </button>
                            <ul className={`dropdown-content ${administrationMenuOpen ? 'show' : ''}`}>
                                <li>
                                    <button onClick={() => handleViewChange("create-user")}>
                                        Create User
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("admin-resource-center")}>Resource Center</button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("import-users")}>
                                        Import Users
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("manage-users")}>
                                        Manage Users
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("config")}>
                                        Configuration
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("speciality-institution-manager")}>
                                        Manage Rotations & Institutions
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("manage-ranks")}>
                                        Manage Ranks
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("manage-curricula")}>
                                        Manage Curricula
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("preceptor-availability")}>
                                        Manage Preceptor Availability
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("all-evaluations")}>
                                        All Evaluations
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("student-attendance")}>
                                        Student Attendance
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("make-announcement")}>
                                        Make Announcement
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => onFormBuilderClick && onFormBuilderClick()}
                                        className="form-builder-btn"
                                    >
                                        Form Builder
                                    </button>
                                </li>
                            </ul>
                        </li>
                    </>
                )}

                {/* Non-Admin Preceptor options */}
                {!isStandaloneAdmin && userType === 'preceptor' && !isAdmin && (
                    <>
                        <li>
                            <button onClick={() => handleViewChange("resource-center")}>
                                Resource Center
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("choices")}>
                                Update Availability
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("schedule")}>
                                View Schedule
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("forms")}>
                                Submit Forms
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("my-evaluations")}>
                                My Evaluations
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("student-attendance")}>
                                Student Attendance
                            </button>
                        </li>
                    </>
                )}

                {/* Standalone Admin: same Scheduling + Administration tabs */}
                {isStandaloneAdmin && (
                    <>
                        {/* Scheduling tab: separate schedule pages + constraints */}
                        {renderSchedulingDropdown()}
                        <li className="dropdown">
                            <button 
                                className={`dropdown-btn ${administrationMenuOpen ? 'active' : ''}`}
                                onClick={handleAdministrationMenuClick}
                            >
                                Administration <span className="arrow">▼</span>
                            </button>
                            <ul className={`dropdown-content ${administrationMenuOpen ? 'show' : ''}`}>
                                <li>
                                    <button onClick={() => handleViewChange("create-user")}>
                                        Create User
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("manage-users")}>
                                        Manage Users
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("import-users")}>
                                        Import Users
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("admin-resource-center")}>
                                        Resource Center
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("config")}>
                                        Configuration
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("speciality-institution-manager")}>
                                        Manage Rotations & Institutions
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("manage-ranks")}>
                                        Manage Ranks
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("manage-curricula")}>
                                        Manage Curricula
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("preceptor-availability")}>
                                        Manage Preceptor Availability
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("all-evaluations")}>
                                        All Evaluations
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("student-attendance")}>
                                        Student Attendance
                                    </button>
                                </li>
                                <li>
                                    <button onClick={() => handleViewChange("make-announcement")}>
                                        Make Announcement
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => onFormBuilderClick && onFormBuilderClick()}
                                        className="form-builder-btn"
                                    >
                                        Form Builder
                                    </button>
                                </li>
                            </ul>
                        </li>
                    </>
                )}

                {/* Student options */}
                {userType === 'student' && (
                    <>
                        <li>
                            <button onClick={() => handleViewChange("resource-center")}>
                                Resource Center
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("choices")}>
                                My Preferences
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("schedule")}>
                                My Schedule
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("forms")}>
                                Submit Attendance
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("my-attendance")}>
                                My Attendance
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("my-evaluations")}>
                                My Evaluations
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("report-center")}>
                                Report Center
                            </button>
                        </li>
                    </>
                )}

                {/* Super Admin specific options */}
                {userType === 'super_admin' && (
                    <>
                        <li>
                            <button onClick={() => handleViewChange("licenses")}>
                                Manage Licenses
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("analytics")}>
                                Analytics
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("organization-applications")}>
                                Organization Applications
                            </button>
                        </li>
                        <li>
                            <button onClick={() => handleViewChange("make-announcement")}>
                                Make Announcement
                            </button>
                        </li>
                    </>
                )}

                <li>
                    <NotificationCenter userType={userType} userId={userId} />
                </li>
                <li>
                    <button className="logout-btn" onClick={handleLogout}>Logout</button>
                </li>
            </ul>
        </nav>
    );
};

export default CollapsibleNavbar;
